"use client";

import { useCallback } from "react";

import { useEstimator } from "../_context/EstimatorContext";
import type { Origin } from "../_logic/types";
import { originKey } from "../_logic/workspace";
import { useUndoableEdit } from "./useUndoableEdit";

/**
 * Removing a destination or a home city from the comparison, each with an Undo toast.
 * @returns `removeDestination` and `removeOrigin`.
 * @throws Never.
 */
export function useRemovePlace(): {
  removeDestination: (city: string) => void;
  removeOrigin: (origin: Origin) => void;
} {
  // Hooks
  const { workspace } = useEstimator();
  const apply = useUndoableEdit();

  // Handlers
  const removeDestination = useCallback(
    (city: string) => apply({ type: "removeDestination", city }, `Removed ${city}`),
    [apply]
  );
  const removeOrigin = useCallback(
    (origin: Origin) =>
      apply(
        {
          type: "setOrigins",
          origins: workspace.origins.filter((entry) => originKey(entry) !== originKey(origin)),
        },
        `Removed ${origin.group ? `${origin.group} (${origin.city})` : origin.city}`
      ),
    [apply, workspace.origins]
  );

  return { removeDestination, removeOrigin };
}
